/**
 * GoogleReviewPrompt — floating widget that asks happy users to leave a
 * Google Business Profile review for Central Rentals Canada.
 *
 * Trigger logic:
 *   Landlord: shows after 30 days since account created + 1 successful rent-collection cycle.
 *   Tenant:   shows after 3 consecutive on-time payments OR 1 resolved maintenance ticket.
 *   Dismissed once => hidden 90 days (localStorage timestamp).
 *   Reviewed once  => hidden forever (localStorage flag).
 *
 * The Google review URL comes from REACT_APP_GOOGLE_REVIEW_URL. If unset,
 * the widget is disabled (no prompt shown) so the app can ship before the
 * Google Business Profile is set up.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Star, X } from "lucide-react";

const REVIEW_URL = process.env.REACT_APP_GOOGLE_REVIEW_URL || "";
const DISMISSED_KEY = "crc_google_review_dismissed_at";
const REVIEWED_KEY = "crc_google_review_reviewed";
const HIDE_DAYS = 90;

export default function GoogleReviewPrompt() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!REVIEW_URL) return;                    // widget disabled until env var set
    if (!user?.role) return;                    // must be signed in
    if (localStorage.getItem(REVIEWED_KEY)) return;
    const dismissedAt = localStorage.getItem(DISMISSED_KEY);
    if (dismissedAt) {
      const daysAgo = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysAgo < HIDE_DAYS) return;
    }

    const role = (user.role || "").toLowerCase();
    (async () => {
      try {
        if (role === "landlord" || role === "admin") {
          // Trigger: 30+ days since account + at least 1 rent payment collected.
          const createdAt = user.created_at ? new Date(user.created_at).getTime() : Date.now();
          const daysSince = (Date.now() - createdAt) / (1000 * 60 * 60 * 24);
          if (daysSince < 30) return;
          const r = await api.get("/autopilot/leases").catch(() => null);
          // Look for at least 1 lease with any past on-time period; even
          // absence-of-late-payments qualifies (no autopilot queue items).
          if (r && (r.data.leases || []).some((l) => !l.days_late)) {
            setVisible(true);
          }
        } else if (role === "tenant") {
          const r = await api.get("/tenant/score").catch(() => null);
          if (r && (r.data.current_streak || 0) >= 3) {
            setVisible(true);
          }
        }
      } catch { /* ignore trigger errors — never crash the app for a review prompt */ }
    })();
  }, [user]);

  if (!visible || !REVIEW_URL) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  };

  const openReview = () => {
    localStorage.setItem(REVIEWED_KEY, "1");
    window.open(REVIEW_URL, "_blank", "noopener,noreferrer");
    setVisible(false);
  };

  return (
    <div
      // Anchored past the sidebar (w-72 = 18rem + 24px gutter) on desktop
      // so it doesn't sit *behind* the fixed left navigation. On mobile
      // there's no sidebar, so we fall back to a plain bottom-left card.
      // Compact width + lifted to bottom-28 on desktop so it clears the
      // "Ask the docs" pill at bottom-6 and never overlaps primary CTAs.
      className="fixed bottom-6 left-6 lg:left-[19.5rem] lg:bottom-28 z-30 max-w-xs bg-white rounded-2xl shadow-xl border border-slate-200 p-4 animate-in fade-in slide-in-from-bottom-4 duration-300"
      role="dialog"
      aria-label="Leave a Trustpilot review"
      data-testid="google-review-prompt"
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="google-review-dismiss"
        className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 p-1"
      >
        <X className="w-4 h-4"/>
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-none">
          <Star className="w-5 h-5 text-emerald-600 fill-emerald-600"/>
        </div>
        <div className="min-w-0">
          <p className="font-display font-bold text-slate-900 text-base">Enjoying Central Rentals?</p>
          <p className="text-sm text-slate-600 mt-1 leading-snug">
            A quick Trustpilot review helps other Canadian landlords and tenants find us. Takes 30 seconds.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              onClick={openReview}
              data-testid="google-review-cta"
              className="text-sm font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              Leave a review
            </button>
            <button
              onClick={dismiss}
              data-testid="google-review-later"
              className="text-sm font-medium px-3 py-1.5 rounded-lg text-slate-500 hover:text-slate-700"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
